import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { CircleAlert, Info, ShieldQuestion } from "lucide-react";
import { MorphingModal } from "./motion/morphing-modal";
import { Button } from "./motion/button/base";

type AlertTone = "info" | "warning" | "error";
type AlertRequest = {
  id: number;
  message: ReactNode;
  title: string;
  tone: AlertTone;
  confirm: boolean;
  resolve?: (accepted: boolean) => void;
};
type AlertApi = {
  confirm: (message: string) => Promise<boolean>;
  show: (message: ReactNode, tone: AlertTone) => () => void;
};
const AlertContext = createContext<AlertApi | null>(null);
const titles: Record<AlertTone, string> = {
  info: "Informação",
  warning: "Atenção",
  error: "Não foi possível concluir",
};

export function AlertProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<AlertRequest[]>([]);
  const requests = useRef<AlertRequest[]>([]);
  const nextId = useRef(0);
  const { pathname } = useLocation();
  const previousPath = useRef(pathname);
  const update = useCallback((next: AlertRequest[]) => {
    requests.current = next;
    setQueue(next);
  }, []);
  const dismiss = useCallback(
    (id: number, accepted = false) => {
      const request = requests.current.find((item) => item.id === id);
      if (!request) return;
      update(requests.current.filter((item) => item.id !== id));
      request.resolve?.(accepted);
    },
    [update],
  );
  const show = useCallback(
    (message: ReactNode, tone: AlertTone) => {
      const id = ++nextId.current;
      update([
        ...requests.current,
        { id, message, tone, title: titles[tone], confirm: false },
      ]);
      return () => dismiss(id);
    },
    [update, dismiss],
  );
  const confirm = useCallback(
    (message: string) =>
      new Promise<boolean>((resolve) => {
        update([
          ...requests.current,
          {
            id: ++nextId.current,
            message,
            tone: "warning",
            title: "Confirmar ação",
            confirm: true,
            resolve,
          },
        ]);
      }),
    [update],
  );
  useEffect(() => {
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;
    const previous = requests.current;
    update([]);
    previous.forEach((request) => request.resolve?.(false));
  }, [pathname, update]);
  useEffect(
    () => () => {
      requests.current.forEach((request) => request.resolve?.(false));
    },
    [],
  );
  const api = useMemo(() => ({ show, confirm }), [show, confirm]);
  return (
    <AlertContext.Provider value={api}>
      {children}
      <AlertOverlay request={queue[0]} dismiss={dismiss} />
    </AlertContext.Provider>
  );
}

function AlertOverlay({
  request,
  dismiss,
}: {
  request?: AlertRequest;
  dismiss: (id: number, accepted?: boolean) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const requestRef = useRef(request);
  requestRef.current = request;
  const id = useId();
  useEffect(() => {
    if (!request) return;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // O alerta pode abrir sobre um formulário em outro modal. Só o alerta superior recebe foco.
    const siblings = Array.from(document.body.children).filter(
      (node): node is HTMLElement => node instanceof HTMLElement && node !== host.current,
    );
    const previousInert = siblings.map((node) => node.inert);
    siblings.forEach((node) => {
      node.inert = true;
    });
    const focusable = () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]',
        ) ?? [],
      );
    const focusFirst = () => (focusable()[0] ?? panel.current)?.focus();
    const frame = requestAnimationFrame(focusFirst);
    const onFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target))
        focusFirst();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Tab" && event.key !== "Escape") return;
      event.stopImmediatePropagation();
      if (event.key === "Escape") {
        event.preventDefault();
        if (requestRef.current) dismiss(requestRef.current.id);
        return;
      }
      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        event.preventDefault();
        panel.current?.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("focusin", onFocus, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("focusin", onFocus, true);
      siblings.forEach((node, index) => {
        node.inert = previousInert[index] ?? false;
      });
      if (previousFocus?.isConnected) previousFocus.focus();
    };
    // Mantém o foco estável enquanto o texto do mesmo alerta é atualizado.
  }, [request?.id, dismiss]);
  return createPortal(
    <div ref={host} className="admin-shell relative z-[200]">
      <MorphingModal
        viewId={request ? String(request.id) : null}
        onClose={() => {
          if (request) dismiss(request.id);
        }}
        placement="center"
        className="max-h-[calc(100dvh-2rem)] max-w-md overflow-y-auto bg-card"
      >
        {request && (
          <div
            ref={panel}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={`${id}-title`}
            aria-describedby={`${id}-message`}
            tabIndex={-1}
          >
            <div
              className={`mb-4 grid size-10 place-items-center rounded-xl border border-border bg-background ${request.tone === "error" ? "text-destructive" : "text-accent"}`}
            >
              {request.confirm ? (
                <ShieldQuestion size={21} aria-hidden />
              ) : request.tone === "error" ? (
                <CircleAlert size={21} aria-hidden />
              ) : (
                <Info size={21} aria-hidden />
              )}
            </div>
            <h2 id={`${id}-title`} className="text-lg font-semibold">
              {request.title}
            </h2>
            <div
              id={`${id}-message`}
              className="mt-3 break-words text-sm leading-6 text-muted-foreground"
            >
              {request.message}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              {request.confirm && (
                <Button
                  variant="secondary"
                  className="rounded-xl"
                  onClick={() => dismiss(request.id)}
                >
                  Cancelar
                </Button>
              )}
              <Button className="rounded-xl" onClick={() => dismiss(request.id, true)}>
                {request.confirm ? "Confirmar" : "Entendi"}
              </Button>
            </div>
          </div>
        )}
      </MorphingModal>
    </div>,
    document.body,
  );
}

function useAlerts() {
  const api = useContext(AlertContext);
  if (!api) throw new Error("AlertProvider ausente.");
  return api;
}
export function useAlertConfirm() {
  return useAlerts().confirm;
}

function messageText(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (isValidElement<{ children?: ReactNode }>(child))
        return messageText(child.props.children);
      return "";
    })
    .join(" ");
}

/**
 * Mensagem persistente na página, que pode ser reaberta como modal.
 *
 * Só falhas (`error`) abrem o modal sozinhas: são o que o DESIGN.md chama de
 * "falhas que exigem atenção imediata" (`role="alert"`). Avisos e informações
 * ficam apenas em linha, como `role="status"`, e abrem no clique.
 *
 * Antes, qualquer tom abria o modal ao montar — então uma dica fixa de
 * formulário (o multiplicador de tempo por linguagem, por exemplo) bloqueava a
 * tela a cada visita, e o mesmo texto aparecia duas vezes: no modal e em
 * linha. Telas com aviso permanente, como a edição de problema durante a
 * prova, abriam um modal por aviso toda vez que o admin entrasse.
 */
export function AlertMessage({
  children,
  tone = "info",
}: {
  children: ReactNode;
  tone?: AlertTone;
}) {
  const { show } = useAlerts();
  const current = useRef(children);
  current.current = children;
  const text = messageText(children);

  useEffect(() => {
    if (tone !== "error") return;
    // `show` devolve o descarte: usá-lo como cleanup evita que o alerta
    // sobreviva à tela que o gerou.
    return show(current.current, tone);
  }, [show, text, tone]);

  return (
    <Button
      variant="ghost"
      // `alert` interrompe o leitor de tela; `status` espera a pausa. Só falha
      // justifica interromper (DESIGN.md, seção 6).
      role={tone === "error" ? "alert" : "status"}
      title="Abrir em destaque"
      className="h-auto justify-start whitespace-normal rounded-lg px-2 py-2 text-left text-sm"
      onClick={() => show(current.current, tone)}
    >
      {tone === "error" ? (
        <CircleAlert size={16} className="shrink-0 text-destructive" aria-hidden />
      ) : (
        <Info size={16} className="shrink-0 text-accent" aria-hidden />
      )}
      <span>{children}</span>
    </Button>
  );
}
